import { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { cn, Button } from '@scio3/ui';
import { useTeamStore } from '@/stores/team-store';

export interface TemplateCardData {
  templateId: string;
  title: string;
  thumbnailUrl: string;
  dimensions: { width: number; height: number };
  tags: string[];
  score?: string;
}

/**
 * Compact card displaying a single template result inside OptiBot chat.
 * - Thumbnail with lazy loading + fallback
 * - "Use Template" button navigates to the Designer editor
 * - External link icon opens in a new tab
 */
export function TemplateCard({
  templateId,
  title,
  thumbnailUrl,
  dimensions,
  tags,
  score,
}: TemplateCardData) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const teamId = useTeamStore((s) => s.activeTeamId);
  const [imgError, setImgError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideoThumbnail = thumbnailUrl?.endsWith('.mp4') ?? false;

  const handleMouseEnter = () => {
    if (isVideoThumbnail) videoRef.current?.play().catch(() => {});
  };

  const handleMouseLeave = () => {
    if (isVideoThumbnail && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const isPortrait = dimensions.height > dimensions.width;
  const templateUrl = `/app/designManagement?template_id=${templateId}&teamId=${teamId}&origin=templates`;
  const dimensionLabel = `${dimensions.width}\u00D7${dimensions.height}`;
  const tagLabel = tags.length > 0 ? tags[0] : '';
  const meta = [dimensionLabel, tagLabel].filter(Boolean).join(' \u00B7 ');

  // Portrait: taller container, narrower width; Landscape: standard wide container
  const thumbContainerClass = cn(
    'shrink-0 overflow-hidden rounded-md bg-muted',
    isPortrait ? 'h-[120px] w-[68px]' : 'h-[68px] w-[120px]',
  );
  const thumbFitClass = cn('h-full w-full', isPortrait ? 'object-contain' : 'object-cover');

  return (
    <div
      className="flex gap-3 rounded-lg border border-border bg-background p-2"
      data-testid="optibot-template-card"
    >
      {/* Thumbnail */}
      <div
        className={thumbContainerClass}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {!imgError && thumbnailUrl ? (
          isVideoThumbnail ? (
            <video
              ref={videoRef}
              src={thumbnailUrl}
              className={thumbFitClass}
              muted
              loop
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              src={thumbnailUrl}
              alt={title}
              loading="lazy"
              className={thumbFitClass}
              onError={() => setImgError(true)}
            />
          )
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
            {dimensionLabel}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
        <div>
          <p className="truncate text-xs font-medium leading-tight">{title}</p>
          <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
            {meta}
            {score ? ` \u00B7 ${score}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            variant="default"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={() => navigate(templateUrl)}
          >
            {t('optibot.templateCard.useTemplate')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            aria-label={t('optibot.templateCard.openInNewTab')}
            onClick={() => window.open(templateUrl, '_blank')}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
